import {
  Injectable,
  NgZone,
  OnDestroy,
} from '@angular/core';
import {
  BehaviorSubject,
  Subscription,
  debounceTime,
  fromEvent,
} from 'rxjs';

const MOBILE_BREAKPOINT = 768;

@Injectable({
  providedIn: 'root',
})
export class SidenavStateService implements OnDestroy {
  private readonly collapsedSubject = new BehaviorSubject(false);
  private readonly mobileOpenSubject = new BehaviorSubject(false);
  private readonly resizeSubscription: Subscription;

  readonly isCollapsed$ = this.collapsedSubject.asObservable();
  readonly isMobileOpen$ = this.mobileOpenSubject.asObservable();

  constructor(private readonly ngZone: NgZone) {
    this.resizeSubscription = fromEvent(window, 'resize').pipe(
      debounceTime(150),
    ).subscribe(() => {
      this.ngZone.run(() => {
        if (this.isMobile && this.isCollapsed) {
          this.setCollapsed(false);
        }

        if (!this.isMobile && this.isMobileOpen) {
          this.setMobileOpen(false);
        }
      });
    });
  }

  get isCollapsed(): boolean {
    return this.collapsedSubject.value;
  }

  get isMobileOpen(): boolean {
    return this.mobileOpenSubject.value;
  }

  get isMobile(): boolean {
    return window.innerWidth < MOBILE_BREAKPOINT;
  }

  toggle(): void {
    if (this.isMobile) {
      this.setMobileOpen(!this.isMobileOpen);
      return;
    }

    this.setCollapsed(!this.isCollapsed);
  }

  setCollapsed(value: boolean): void {
    this.collapsedSubject.next(value);
    document.body.classList.toggle('qx-sidenav-collapsed', value);
  }

  setMobileOpen(value: boolean): void {
    this.mobileOpenSubject.next(value);
    document.body.classList.toggle('qx-sidenav-mobile-open', value);
  }

  closeMobile(): void {
    if (this.isMobileOpen) {
      this.setMobileOpen(false);
    }
  }

  ngOnDestroy(): void {
    this.resizeSubscription.unsubscribe();
  }
}
